/**
 * client/src/components/VisibilityMenu.tsx — private · unlisted · public, and the one of them that
 * is a *request* rather than a setting.
 *
 * ── The three words, and what each of them actually means ───────────────────────────────────────
 *
 * They are not a scale, and writing them as one ("more open" → "most open") is how somebody picks
 * `unlisted` believing it is a weaker `private`. §6.3:
 *
 *   · **private**  — only you. Nothing else opens it, not even a guessed id.
 *   · **unlisted** — anyone holding the id can read it, and it appears in NO listing, search or
 *                    lobby. `canList` vs `canSee` *is* this row, and `books.id` (~83 bits) is the
 *                    capability, which is why an unlisted book has no `slug`.
 *   · **public**   — world-readable, indexable, in the lobby. **Requires Karti's approval**, so
 *                    choosing it here writes `publicRequestedAt` and leaves the book unlisted.
 *
 * ── LAW 13 ──────────────────────────────────────────────────────────────────────────────────────
 *
 * Every row this component draws comes from `canEdit` in `shared/access.ts` — the same function the
 * server enforces with. Not a `mine` boolean the parent worked out, not `user.id === book.ownerId`
 * inlined here. Two implementations of one rule diverge on the case nobody screenshotted, and the
 * case here is a god editing somebody else's book.
 *
 * ── Mobile-first at 390 px ──────────────────────────────────────────────────────────────────────
 *
 * `.ab-segment-full` is a three-up row that fills the width and stacks nothing, so the control is one
 * thumb-sized tap per option at phone width. No new CSS — every class here already exists in
 * `ui/app.css`, which is deliberate: a component that ships its own stylesheet is the third copy of a
 * button that books.karti.ai has a monument to.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { canEdit } from "../../../shared/access.ts";
import type { BookMeta, BookVisibility } from "../../../shared/doc.ts";
import type { MaybeActor } from "../../../shared/actor.ts";
import { ApiError, abApi } from "../lib/api.ts";

const OPTIONS: { value: BookVisibility; label: string; blurb: string }[] = [
  { value: "private", label: "Private", blurb: "Only you. Nothing else opens it." },
  {
    value: "unlisted",
    label: "Unlisted",
    blurb: "Anyone holding the link can read and listen. It appears in no shelf, no search, no lobby.",
  },
  {
    value: "public",
    label: "Public",
    blurb: "On the public shelf and indexable. Karti approves this one by hand.",
  },
];

export function VisibilityMenu({
  book,
  actor,
  onChanged,
}: {
  book: BookMeta;
  actor: MaybeActor;
  /** Called with the fresh row so the parent can re-render without a second fetch. */
  onChanged?: (book: BookMeta) => void;
}) {
  const [busy, setBusy] = useState<BookVisibility | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const mayEdit = canEdit(book, actor);
  const pending = book.publicRequestedAt !== null && book.visibility !== "public";

  async function set(want: BookVisibility) {
    if (busy !== null || want === book.visibility) return;
    setBusy(want);
    setError(null);
    setNote(null);
    try {
      const res = await abApi.patchBook(book.id, { visibility: want });
      onChanged?.(res.book);
      setNote(
        res.publicRequested
          ? // ⚠️ The honest sentence, not "Done". The user asked for public and did not get it, and a
            // screen that says nothing leaves them believing a private book is on the internet — or,
            // worse, believing a public one is still private.
            "Asked Karti to make this public. Until he approves it, the book is unlisted — anyone with the link can read it, and it is in no listing."
          : res.evicted
            ? `Changed. ${res.evicted} ${res.evicted === 1 ? "person was" : "people were"} reading it in a room and lost access.`
            : null,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change that.");
    } finally {
      setBusy(null);
    }
  }

  if (!mayEdit) {
    // Not a disabled control. A greyed-out visibility picker on somebody else's book invites them to
    // press it and tells them nothing; the chip says what is true and stops.
    return (
      <p className="ab-hint ab-hint-quiet">
        {book.visibility === "public" ? "A public book." : book.visibility === "unlisted" ? "Shared by link." : "Private."}
      </p>
    );
  }

  const current = OPTIONS.find((o) => o.value === book.visibility) ?? OPTIONS[0]!;

  return (
    <div className="ab-field">
      <label htmlFor="ab-visibility">Who can open this</label>

      <div className="ab-segment ab-segment-full" id="ab-visibility" role="group" aria-label="Who can open this">
        {OPTIONS.map((o) => {
          // `public` stays highlighted while it is only *requested*, because the person pressed it and
          // the request is the state they are in. The blurb below is what says it has not landed.
          const on = o.value === book.visibility || (o.value === "public" && pending);
          return (
            <button
              key={o.value}
              type="button"
              className={on ? "ab-segment-item is-on" : "ab-segment-item"}
              aria-pressed={on}
              disabled={busy !== null}
              onClick={() => void set(o.value)}
            >
              {busy === o.value ? "…" : o.label}
            </button>
          );
        })}
      </div>

      <p className="ab-hint">{pending ? "Waiting on Karti. Unlisted until then." : current.blurb}</p>

      {note && <p className="ab-note ab-note-warn">{note}</p>}
      {error && <p className="ab-hint ab-hint-bad">{error}</p>}

      {book.visibility === "public" && book.slug && (
        // The slug exists ONLY on a public book (§6.3) — an unlisted book addressed by a guessable
        // name is not unlisted — so showing it here doubles as the honest signal that the book really
        // did become world-readable.
        <p className="ab-hint ab-hint-quiet">Public name: {book.slug}</p>
      )}
    </div>
  );
}

/**
 * The one sentence a dead share link earns.
 *
 * `GET /s/:token` redirects to `/?share=invalid` for **five** different states — revoked, expired,
 * the book deleted, the book taken down, and a token that never existed — deliberately, because
 * distinguishing them is an oracle on a 120-bit secret. So this says the one thing that is true of
 * all five and names the fix, per AGENTS.md's refusal rule: *"couldn't process this"* is how somebody
 * taps the same dead link four times.
 *
 * Rendered by `<AppFrame>` and by the landing page, so it is on screen wherever the redirect lands —
 * signed in or not.
 */
export function ShareNotice() {
  const [params, setParams] = useSearchParams();
  if (params.get("share") !== "invalid") return null;

  /**
   * ⚠️ The button is in a **block** under the text, not a second flex child beside it.
   *
   * `.ab-note` is `display: flex`, and a bare `<button>` as its second child gets shrunk by the
   * flex algorithm until it is jammed against the right edge — which at 390 px is a Dismiss control
   * half off the screen with the sentence wrapping around it. Found by screenshotting at phone width,
   * not by reading the markup; it looks perfectly fine at 1280.
   */
  return (
    <div className="ab-note ab-note-warn">
      <div>
        <span>
          That share link isn&rsquo;t working any more — it was revoked, it expired, or the book is
          gone. Ask whoever sent it for a new one.
        </span>
        <div className="ab-actions">
          <button
            type="button"
            className="ab-btn ab-btn-ghost ab-btn-sm"
            onClick={() => {
              // `replace`, so Back does not walk into the notice again. The parameter is the whole of
              // the state; clearing it is the dismissal.
              params.delete("share");
              setParams(params, { replace: true });
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
